import { memo, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { BodyCopy } from "../../components/BodyCopy";
import { Navbar } from "../../components/Navbar";
import { Popup } from "../../components/Popup";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { TermsMarkdown } from "../../components/request/TermsMarkdown";
import { useSupportSheet } from "../../components/SupportSheet";
import { TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import "../../styles/legal.style.css";

export type SmsTermsChrome = "need-help" | "plain";

const SmsTermsPopup = ({
  titleColor,
  bodyCopy,
  background,
  backgroundBtn,
  textColorBtn,
  onViewPolicy,
}: {
  titleColor: string;
  bodyCopy: string;
  background: string;
  backgroundBtn: string;
  textColorBtn: string;
  onViewPolicy: () => void;
}) => {
  const { t } = useTranslation("smsTerms");

  return (
    <Popup
      visible
      showClose={false}
      dismissOnOutsideClick={false}
      onClose={() => undefined}
    >
      <section className="cancel-confirm-slot sms-terms-popup">
        <Title title={t("title")} align="center" color={bodyCopy} />
        <BodyCopy textColor={bodyCopy}>
          {t("intro", { phone: t("maskedNumber") })}
        </BodyCopy>
        <div className="sms-terms-detail-row">
          <GradientText
            as="button"
            className="link sms-terms-detail-link"
            textColor={titleColor}
            onClick={onViewPolicy}
          >
            {t("viewPolicy")}
          </GradientText>
        </div>
        <div className="cancel-confirm-actions">
          <Button
            type="button"
            size="large"
            variant="outline"
            background={backgroundBtn}
            color={bodyCopy}
            style={{ backgroundColor: background }}
            onClick={() => undefined}
          >
            {t("decline")}
          </Button>
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => undefined}
          >
            {t("accept")}
          </Button>
        </div>
      </section>
    </Popup>
  );
};

const SmsTermsPage = memo(
  ({
    needHelp = false,
    namespace = "send",
    detailRoute,
  }: {
    needHelp?: boolean;
    namespace?: string;
    detailRoute: string;
  }) => {
    const { t } = useTranslation("smsTerms");
    const { t: tFlow } = useTranslation(namespace);
    const navigate = useNavigate();
    const { theme } = useStore();
    const {
      title,
      bodyCopy,
      dropzoneColors,
      background,
      backgroundBtn,
      textColorBtn,
    } = theme.whiteView;
    const { openSupport, supportSheet } = useSupportSheet(namespace);

    return (
      <>
        <AuraLayout
          colorConfig={theme.whiteView}
          className={`sms-terms-page${needHelp ? " need-help-header" : ""}`}
        >
          <Layout.Header>
            {needHelp ? (
              <Navbar
                title={tFlow("common.needHelp")}
                align="right"
                noBackButton
                onIconClick={openSupport}
                icon={<SupportIcon size={35} colors={dropzoneColors} />}
              />
            ) : (
              <Navbar title={t("title")} align="center" color={title} noBackButton />
            )}
          </Layout.Header>
          <Layout.Content />
        </AuraLayout>
        <SmsTermsPopup
          titleColor={title}
          bodyCopy={bodyCopy}
          background={background}
          backgroundBtn={backgroundBtn}
          textColorBtn={textColorBtn}
          onViewPolicy={() => navigate(detailRoute)}
        />
        {needHelp ? supportSheet : null}
      </>
    );
  },
);

/** Same shell as a Terms & Conditions detail page. */
const SmsTermsDetailPage = memo(
  ({
    flavorA = false,
    backRoute,
  }: {
    flavorA?: boolean;
    backRoute: string;
  }) => {
    const { t } = useTranslation("smsTerms");
    const navigate = useNavigate();
    const { theme } = useStore();
    const { bodyCopy } = theme.whiteView;

    return (
      <AuraLayout
        colorConfig={theme.whiteView}
        className={`term-detail-page${flavorA ? " flavor-a" : ""}`}
      >
        <Layout.Header>
          <Navbar
            onBackClick={() => navigate(backRoute)}
            title={t("detailTitle")}
            align="center"
            color={TITLE_COLOR}
          />
        </Layout.Header>
        <Layout.Content isOverflowauto>
          <ScrollFadeIndicator color={bodyCopy} />
          <section className="term-detail-wrapper" style={{ color: bodyCopy }}>
            <TermsMarkdown content={t("detailBody")} />
          </section>
        </Layout.Content>
      </AuraLayout>
    );
  },
);

/** Stable gallery page. The opt-in is a popup over the flow chrome. */
export const smsTermsScreen = (
  chrome: SmsTermsChrome,
  namespace: string | undefined,
  detailRoute: string,
): ComponentType => {
  const Page = memo(() => (
    <SmsTermsPage
      needHelp={chrome === "need-help"}
      namespace={namespace}
      detailRoute={detailRoute}
    />
  ));
  Page.displayName = `SmsTerms-${chrome}`;
  return Page;
};

/** Full-page policy, same format as the other terms detail screens. */
export const smsTermsDetailScreen = (
  chrome: SmsTermsChrome,
  backRoute: string,
): ComponentType => {
  const Page = memo(() => (
    <SmsTermsDetailPage flavorA={chrome === "plain"} backRoute={backRoute} />
  ));
  Page.displayName = `SmsTermsDetail-${chrome}`;
  return Page;
};
